'use client';

import { useCallback, useRef } from 'react';
import Link from 'next/link';
import { Camera, FileText } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { Project } from '@/types/project';
import {
  hasImageFile,
  isSystemReference,
  type ProjectReference,
} from '@/types/project-reference';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { useProjectReferences } from '@/features/project-reference/hooks/use-project-references';
import { useProjectControls } from '@/features/control/hooks/use-project-controls';
import type { Control } from '@/types/concept';
import {
  SectionHeader,
  ScrollableCarousel,
  SectionPanel,
  RequiredIndicator,
  PANEL_HEIGHT,
} from './section-components';
import { PhotoFrameCorners, FileDropTarget } from '@/shared/ui';
// Deep import on purpose: the angles-overview barrel pulls `@/features/project`
// (SectionAddButton), so importing it from here would close a barrel cycle.
import {
  BriefIntake,
  BRIEF_INTAKE_ACCEPT,
  type BriefIntakeRef,
} from '@/features/angles-overview/ui/BriefIntake';
import { GoalsQualityIndicator } from './GoalsQualityIndicator';

// Square Brief material tile edge — matches the carousel card height used by
// the sibling sections (PANEL_HEIGHT - 24), kept square per the shared spec.
const TILE_SIZE = PANEL_HEIGHT - 24;

interface ProjectGoalsSectionProps {
  project: Project;
  projectId: string;
}

/**
 * Full Brief section on the building page — same anatomy as Designs /
 * Estimates / Blueprints: SectionHeader (title + count + indicator + chevron
 * navigating to the Brief page) over a horizontal carousel of the brief
 * materials in the Brief page's own group order (views, sketches, brief
 * documents, references), or an Estimates-style empty panel inviting the first
 * photo upload.
 */
export function ProjectGoalsSection({
  project,
  projectId,
}: ProjectGoalsSectionProps) {
  const t = useTranslations('project');
  const goalsHref = `/project/${projectId}/goals`;
  const isArchived = !!project.attributes.archived_at;

  // Files dropped onto the section feed the same intake as the Brief page
  // (photos → views, drawings/docs → documents); the carousel reconciles from
  // the shared query caches.
  const intakeRef = useRef<BriefIntakeRef>(null);
  const handleDroppedFiles = useCallback((files: File[]) => {
    intakeRef.current?.processFiles(files);
  }, []);

  const goalsText = project.attributes.goals?.trim() || '';
  const hasGoals = goalsText.length > 0;
  // Brief now hosts the building's views (photos), so the orange required-dot is
  // the onboarding nudge for the two essential empty tasks: no view uploaded
  // yet, or an empty brief.
  const hasViews = (project.attributes.angles_count ?? 0) > 0;
  const score = project.attributes.goals_quality_score ?? null;
  const pending = !!project.attributes.is_goals_evaluation_pending;

  // Materials, in the Brief page's group order: views (angles) → sketches
  // (controls) → brief documents → references. Same query keys/fetchers as the
  // Brief page, so caches are shared and existing WS/purchase invalidation
  // paths keep both in sync.
  const { data: angles = [] } = useProjectAngles(projectId);
  const { data: briefReferences = [] } = useProjectReferences(projectId, {
    section: 'brief',
  });
  const { data: inspirationReferences = [] } = useProjectReferences(projectId, {
    section: 'reference',
  });
  const { data: controls = [] } = useProjectControls(projectId);
  const briefDocuments = briefReferences.filter(
    (ref) => !isSystemReference(ref)
  );
  const references = inspirationReferences.filter(
    (ref) => !isSystemReference(ref)
  );
  const materialsCount =
    angles.length + controls.length + briefDocuments.length + references.length;

  // Backdrop photo for a sketch: the photo of its owning view, so a transparent
  // stroke layer stays readable (same resolution as the Brief page).
  const angleUrlById = new Map(
    angles.map((angle) => [angle.id, angle.attributes.file_url])
  );
  const backdropUrl = (control: Control) => {
    const angleId =
      control.attributes.backdrop_angle_id ??
      control.relationships?.angle?.data?.id;
    return angleId ? angleUrlById.get(angleId) ?? undefined : undefined;
  };

  const indicator =
    hasViews && hasGoals ? (
      // Has views and a filled brief → GSM signal-strength of the brief.
      // Orange is reserved exclusively for the empty-task indicator.
      <GoalsQualityIndicator
        score={score}
        hasGoals
        pending={pending}
        size="sm"
      />
    ) : (
      <RequiredIndicator />
    );

  return (
    <FileDropTarget
      onFiles={handleDroppedFiles}
      accept={BRIEF_INTAKE_ACCEPT}
      disabled={isArchived}
      className="space-y-4"
      overlayClassName="-inset-3 rounded-2xl"
    >
      <SectionHeader
        title={t('projectGoals')}
        count={materialsCount > 0 ? materialsCount : undefined}
        contextContent={indicator}
        actions={[{ label: t('projectGoals'), href: goalsHref }]}
      />
      {materialsCount > 0 ? (
        <ScrollableCarousel>
          {angles.map((angle) => (
            <Link
              key={`angle-${angle.id}`}
              href={goalsHref}
              className="relative flex-shrink-0 rounded-lg overflow-hidden bg-foreground/5"
              style={{ width: TILE_SIZE, height: TILE_SIZE }}
            >
              {angle.attributes.file_url && (
                <>
                  <img
                    src={angle.attributes.file_url}
                    alt={angle.attributes.name || ''}
                    className="w-full h-full object-cover"
                  />
                  {/* Same photo frame as the Brief page view tiles */}
                  <PhotoFrameCorners />
                </>
              )}
            </Link>
          ))}
          {controls.map((control) => (
            <BriefSketchTile
              key={`control-${control.id}`}
              control={control}
              backdropUrl={backdropUrl(control)}
              href={goalsHref}
            />
          ))}
          {briefDocuments.map((reference) => (
            <BriefDocumentTile
              key={`doc-${reference.id}`}
              reference={reference}
              href={goalsHref}
            />
          ))}
          {references.map((reference) => (
            <BriefDocumentTile
              key={`ref-${reference.id}`}
              reference={reference}
              href={goalsHref}
            />
          ))}
        </ScrollableCarousel>
      ) : (
        // Brand-accent border marks the Brief empty panel as the place to
        // start (empty state only, never on the filled carousel).
        <SectionPanel className="border-2 border-primary">
          <Link
            href={goalsHref}
            className="flex h-full w-full flex-col items-center justify-center gap-2"
          >
            <Camera
              className="h-9 w-9 text-muted-foreground/50"
              strokeWidth={1.5}
            />
            <span className="text-card-meta text-muted-foreground/70">
              {t('briefEmpty')}
            </span>
          </Link>
        </SectionPanel>
      )}
      {/* Headless intake (renders display:none), kept last so the section's
          space-y rhythm is not shifted by an invisible first child. */}
      {!isArchived && <BriefIntake ref={intakeRef} projectId={projectId} />}
    </FileDropTarget>
  );
}

/**
 * Square carousel tile for a brief document. An image-backed document (e.g. a
 * satellite/cadastral map) shows the actual picture; a file document (PDF/txt/
 * md) mirrors the Brief page's doc tile look — file icon + name.
 */
function BriefDocumentTile({
  reference,
  href,
}: {
  reference: ProjectReference;
  href: string;
}) {
  const fileName =
    reference.attributes.metadata?.extraction?.document_title ||
    reference.attributes.metadata?.original_filename ||
    reference.attributes.original_filename ||
    '';
  const imageUrl = reference.attributes.file_url;

  return (
    <Link
      href={href}
      className="relative flex-shrink-0 rounded-lg overflow-hidden bg-foreground/5"
      style={{ width: TILE_SIZE, height: TILE_SIZE }}
    >
      {hasImageFile(reference) && imageUrl ? (
        <img
          src={imageUrl}
          alt={fileName}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-muted-foreground">
          <FileText className="h-14 w-14" />
          <span className="text-card-meta line-clamp-2 text-center">
            {fileName}
          </span>
        </div>
      )}
    </Link>
  );
}

/**
 * Square carousel tile for a sketch (a project control, incl. unused drafts) —
 * simplified mirror of the Brief page's sketch tile: flat surface so strokes
 * read cleanly, the owning view's photo desaturated underneath.
 */
function BriefSketchTile({
  control,
  backdropUrl,
  href,
}: {
  control: Control;
  backdropUrl?: string;
  href: string;
}) {
  const url = control.attributes.file?.url;

  return (
    <Link
      href={href}
      className="relative flex-shrink-0 rounded-lg overflow-hidden border border-border"
      style={{ width: TILE_SIZE, height: TILE_SIZE }}
    >
      <div className="absolute inset-0 bg-white dark:bg-black" />
      {backdropUrl && (
        <img
          src={backdropUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30 saturate-[0.25] brightness-95"
        />
      )}
      {url && (
        <img
          src={url}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </Link>
  );
}
